/**
 * Utility to extract PostHog session_id, distinct_id, and current URL for injecting into HTTP headers.
 *
 * This module provides functions to retrieve PostHog analytics context (session_id, distinct_id, and current_url)
 * from the PostHog SDK and format them as HTTP headers for server-side tracking.
 */

// eslint-disable-next-line import/no-named-as-default
import posthog from "posthog-js";
import { addWarning } from "@wayflyer/blackbox-fe";

/**
 * Gets the PostHog session ID, distinct ID, and current URL.
 *
 * This function retrieves the session_id and distinct_id from the PostHog SDK,
 * and the current URL from window.location.href.
 * These values are used by the backend to associate API requests with PostHog analytics events.
 *
 * @returns An object containing session_id, distinct_id, and current_url, or null values if not available
 */
export function getPostHogContext(): {
  sessionId: string | null;
  distinctId: string | null;
  currentUrl: string | null;
} {
  try {
    // Get the session ID using PostHog's get_session_id() method
    const sessionId = posthog.get_session_id();

    // Get the distinct ID using PostHog's get_distinct_id() method
    const distinctId = posthog.get_distinct_id();

    // Get the current URL from window.location.href
    const currentUrl = typeof window !== "undefined" ? window.location.href : null;

    return {
      sessionId: sessionId || null,
      distinctId: distinctId || null,
      currentUrl: currentUrl || null,
    };
  } catch (error) {
    addWarning("Failed to get PostHog context:", { error });
    return { sessionId: null, distinctId: null, currentUrl: null };
  }
}

/**
 * Gets HTTP headers containing PostHog session, user context, and current URL.
 *
 * These headers are used by the backend middleware to associate API requests
 * with PostHog analytics events, enabling proper session tracking, user attribution,
 * and tracking which frontend page triggered backend events.
 *
 * @returns An object with x-session-id, x-distinct-id, and x-current-url headers, or an empty object if PostHog is unavailable
 */
export function getPostHogHeaders(): Record<string, string> {
  const { sessionId, distinctId, currentUrl } = getPostHogContext();
  const headers: Record<string, string> = {};

  if (sessionId) {
    headers["x-session-id"] = sessionId;
  }

  if (distinctId) {
    headers["x-distinct-id"] = distinctId;
  }

  if (currentUrl) {
    headers["x-current-url"] = currentUrl;
  }

  return headers;
}
